import {
  LANGUAGES,
  MENTION_SOURCE_TYPES,
  SEARCH_TYPES,
  SOURCE,
  SOURCES,
  SearchTypes,
} from "@/constants";
import z from "zod";
import { QueryIn } from "./requestUtils";
import { useLingui } from "@lingui/react/macro";
import { useMemo } from "react";

const optionTypes = [
  SEARCH_TYPES.BRAND,
  SEARCH_TYPES.INDUSTRY_OR_AREA_OF_INTEREST,
  SEARCH_TYPES.COMPETITORS,
  SEARCH_TYPES.CAMPAIGN_OR_PROJECT,
  SEARCH_TYPES.KEY_PEOPLE,
  SEARCH_TYPES.OTHER,
] as const;

export type QueryBuilderForm = {
  title: string;
  type: SearchTypes;
  industry: string | null;
  startDate: Date;
  endDate?: Date;
  queries: Array<Omit<QueryIn, "uuid">>;
};

export function useQueryBuilderFormSchema() {
  const { t } = useLingui();

  const queryBuilderFormSchema = useMemo(() => {
    const querySchema = z.object({
      uuid: z.string().nullable().optional(),
      keywords: z
        .array(z.string())
        .min(1, { message: t`Vänligen ange minst 1 sökord` })
        .optional(),
      languages: z.array(z.enum(LANGUAGES)).optional(),
      sources: z.array(z.enum(SOURCES)).optional(),
      tags: z.array(z.string()).optional(),
      nearKeywords: z.array(z.string()).optional(),
      nearDistance: z
        .number()
        .positive({
          message: t`Avståndet måste vara positivt`,
        })
        .optional(),
      nearNearKeywords: z.array(z.string()).optional(),
      notNearKeywords: z.array(z.string()).optional(),
      notNearDistance: z
        .number()
        .positive({
          message: t`Avståndet måste vara positivt`,
        })
        .optional(),
      includeChannelNames: z.array(z.string().nullable().optional()).optional(),
      excludeChannelNames: z.array(z.string().nullable().optional()).optional(),
      minReachByChannelType: z
        .union([
          z.object({
            [SOURCE.YOUTUBE]: z.number().optional(),
            [SOURCE.INSTAGRAM]: z.number().optional(),
            [SOURCE.TIKTOK]: z.number().optional(),
            [SOURCE.NEWS]: z.number().optional(),
            [SOURCE.TWITTER]: z.number().optional(),
            [SOURCE.FACEBOOK]: z.number().optional(),
            has_custom_values: z.boolean(),
          }),
          z.object({ no_values: z.literal(true) }),
        ])
        .optional(),
      excludeTopics: z.array(z.string()).optional(),
      mentionTypes: z.enum(MENTION_SOURCE_TYPES).optional(),
      includePaywalled: z.boolean().optional(),
    });

    return z.object({
      title: z
        .string()
        .min(1, { message: t`Vänligen lägg till en titel för din bevakning` }),
      type: z.enum(optionTypes),
      industry: z.string().nullable(),
      startDate: z.date(),
      endDate: z.date().optional(),
      queries: z.array(querySchema),
    });
  }, [t]);

  return queryBuilderFormSchema;
}
